import { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { supabaseData } from './supabaseData';
import {
  X, ArrowRight, Search, Package, Loader, Save, AlertTriangle,
  Warehouse, Building2, Truck, MapPin
} from 'lucide-react';
import { useModal } from './AppModal';
import { useTheme } from './ThemeContext';

const locationTypeIcons = {
  warehouse: Warehouse,
  office: Building2,
  truck: Truck
};

export default function TransferModal({ companyId, onClose, onComplete }) {
  const { colors } = useTheme();
  const { showError, showSuccess } = useModal();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [locations, setLocations] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [stock, setStock] = useState({});

  // Transfer state
  const [fromLocationId, setFromLocationId] = useState('');
  const [toLocationId, setToLocationId] = useState('');
  const [selectedItemId, setSelectedItemId] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState('');

  // Item picker
  const [itemSearch, setItemSearch] = useState('');

  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadData();
  }, [companyId]);

  // Load stock for selected from location
  useEffect(() => {
    if (fromLocationId) {
      loadStockForLocation(fromLocationId);
    }
  }, [fromLocationId]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load locations
      const { data: locationData } = await supabaseData
        .from('locations')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('is_default', { ascending: false })
        .order('name');

      setLocations(locationData || []);

      // Set default from location
      if (locationData?.length > 0) {
        const defaultLoc = locationData.find(l => l.is_default) || locationData[0];
        setFromLocationId(defaultLoc.id);

        // Set default to location (first non-default)
        const otherLoc = locationData.find(l => l.id !== defaultLoc.id);
        if (otherLoc) {
          setToLocationId(otherLoc.id);
        }
      }

      // Load inventory items
      const { data: items } = await supabaseData
        .from('inventory_items')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('name');

      setInventoryItems(items || []);

    } catch (error) {
      console.error('Error loading data:', error);
      showError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadStockForLocation = async (locationId) => {
    try {
      const { data } = await supabaseData
        .from('inventory_stock')
        .select('*')
        .eq('company_id', companyId)
        .eq('location_id', locationId);

      const stockMap = {};
      (data || []).forEach(s => {
        stockMap[s.inventory_item_id] = s.quantity_on_hand || 0;
      });
      setStock(stockMap);

      // Reset selected item if it has no stock at new location
      if (selectedItemId && (stockMap[selectedItemId] || 0) <= 0) {
        setSelectedItemId('');
        setQuantity(1);
      }
    } catch (error) {
      console.error('Error loading stock:', error);
    }
  };

  const getLocationIcon = (type) => {
    return locationTypeIcons[type] || MapPin;
  };

  const getAvailableQuantity = () => {
    if (!selectedItemId) return 0;
    return stock[selectedItemId] || 0;
  };

  const filteredItems = inventoryItems.filter(item => {
    const hasStock = (stock[item.id] || 0) > 0;
    const matchesSearch = item.name?.toLowerCase().includes(itemSearch.toLowerCase());
    return hasStock && matchesSearch;
  });

  const selectedItem = inventoryItems.find(i => i.id === selectedItemId);
  const availableQty = getAvailableQuantity();

  const handleTransfer = async () => {
    if (!fromLocationId || !toLocationId) {
      showError('Please select both locations');
      return;
    }

    if (fromLocationId === toLocationId) {
      showError('From and To locations must be different');
      return;
    }

    if (!selectedItemId) {
      showError('Please select an item to transfer');
      return;
    }

    if (quantity <= 0) {
      showError('Please enter a valid quantity');
      return;
    }

    if (quantity > availableQty) {
      showError(`Only ${availableQty} available to transfer`);
      return;
    }

    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();

      // 1. Reduce stock at from location
      const { data: fromStock } = await supabaseData
        .from('inventory_stock')
        .select('id, quantity_on_hand')
        .eq('inventory_item_id', selectedItemId)
        .eq('location_id', fromLocationId)
        .single();

      if (!fromStock) {
        throw new Error('No stock record found at source location');
      }

      const newFromQty = (fromStock.quantity_on_hand || 0) - quantity;

      await supabaseData
        .from('inventory_stock')
        .update({
          quantity_on_hand: newFromQty,
          updated_at: new Date().toISOString()
        })
        .eq('id', fromStock.id);

      // 2. Increase stock at to location (upsert)
      const { data: toStock } = await supabaseData
        .from('inventory_stock')
        .select('id, quantity_on_hand')
        .eq('inventory_item_id', selectedItemId)
        .eq('location_id', toLocationId)
        .maybeSingle();

      if (toStock) {
        // Update existing
        await supabaseData
          .from('inventory_stock')
          .update({
            quantity_on_hand: (toStock.quantity_on_hand || 0) + quantity,
            updated_at: new Date().toISOString()
          })
          .eq('id', toStock.id);
      } else {
        // Create new
        await supabaseData
          .from('inventory_stock')
          .insert({
            company_id: companyId,
            inventory_item_id: selectedItemId,
            location_id: toLocationId,
            quantity_on_hand: quantity
          });
      }

      // 3. Create transfer_out transaction
      await supabaseData
        .from('inventory_transactions')
        .insert({
          company_id: companyId,
          inventory_item_id: selectedItemId,
          location_id: fromLocationId,
          transaction_type: 'transfer_out',
          quantity_change: -quantity,
          quantity_before: fromStock.quantity_on_hand,
          quantity_after: newFromQty,
          transfer_location_id: toLocationId,
          performed_by: user?.id,
          notes: notes || `Transfer to ${locations.find(l => l.id === toLocationId)?.name}`
        });

      // 4. Create transfer_in transaction
      await supabaseData
        .from('inventory_transactions')
        .insert({
          company_id: companyId,
          inventory_item_id: selectedItemId,
          location_id: toLocationId,
          transaction_type: 'transfer_in',
          quantity_change: quantity,
          quantity_before: toStock?.quantity_on_hand || 0,
          quantity_after: (toStock?.quantity_on_hand || 0) + quantity,
          transfer_location_id: fromLocationId,
          performed_by: user?.id,
          notes: notes || `Transfer from ${locations.find(l => l.id === fromLocationId)?.name}`
        });

      const fromName = locations.find(l => l.id === fromLocationId)?.name;
      const toName = locations.find(l => l.id === toLocationId)?.name;
      showSuccess(`Transferred ${quantity} ${selectedItem?.name} from ${fromName} to ${toName}`);

      onComplete && onComplete();
      onClose();

    } catch (error) {
      console.error('Error transferring stock:', error);
      showError('Failed to transfer: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}>
        <div style={{
          background: colors.bgCard,
          borderRadius: '16px',
          padding: '2rem',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <Loader size={32} color={colors.accent} style={{ animation: 'spin 1s linear infinite' }} />
          <span style={{ color: colors.text }}>Loading...</span>
        </div>
      </div>
    );
  }

  if (locations.length < 2) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '1rem'
        }}
        onClick={onClose}
      >
        <div
          style={{
            background: colors.bgCard,
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: '400px',
            textAlign: 'center'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <AlertTriangle size={48} color={colors.warning} style={{ marginBottom: '1rem' }} />
          <h2 style={{ margin: '0 0 0.5rem', color: colors.text }}>Need Multiple Locations</h2>
          <p style={{ color: colors.textMuted, marginBottom: '1.5rem' }}>
            You need at least 2 locations to transfer stock. Add more locations first.
          </p>
          <button
            onClick={onClose}
            style={{
              padding: '0.75rem 2rem',
              background: colors.accent,
              border: 'none',
              borderRadius: '8px',
              color: '#fff',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            Got it
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '1rem'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: colors.bgCard,
          borderRadius: '16px',
          width: '100%',
          maxWidth: '500px',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <h2 style={{ margin: 0, color: colors.text, fontSize: '1.25rem' }}>
            Transfer Stock
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: colors.textMuted,
              cursor: 'pointer'
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '1.5rem' }}>
          {/* From/To Locations */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            marginBottom: '1.5rem'
          }}>
            {/* From */}
            <div style={{ flex: 1 }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: colors.textMuted,
                fontSize: '0.85rem'
              }}>
                From
              </label>
              <select
                value={fromLocationId}
                onChange={(e) => setFromLocationId(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: colors.bgInput,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  color: colors.text
                }}
              >
                {locations.map(loc => {
                  const Icon = getLocationIcon(loc.location_type);
                  return (
                    <option key={loc.id} value={loc.id}>
                      {loc.name}
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Arrow */}
            <div style={{
              paddingTop: '1.5rem',
              color: colors.accent
            }}>
              <ArrowRight size={24} />
            </div>

            {/* To */}
            <div style={{ flex: 1 }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: colors.textMuted,
                fontSize: '0.85rem'
              }}>
                To
              </label>
              <select
                value={toLocationId}
                onChange={(e) => setToLocationId(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: colors.bgInput,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  color: colors.text
                }}
              >
                {locations
                  .filter(l => l.id !== fromLocationId)
                  .map(loc => (
                    <option key={loc.id} value={loc.id}>
                      {loc.name}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          {/* Item Selection */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              color: colors.text,
              fontWeight: 'bold'
            }}>
              Item to Transfer
            </label>

            {/* Search */}
            <div style={{ position: 'relative', marginBottom: '0.75rem' }}>
              <Search size={18} style={{
                position: 'absolute',
                left: '0.75rem',
                top: '50%',
                transform: 'translateY(-50%)',
                color: colors.textMuted
              }} />
              <input
                type="text"
                placeholder="Search items..."
                value={itemSearch}
                onChange={(e) => setItemSearch(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem 0.75rem 0.75rem 2.5rem',
                  background: colors.bgInput,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  color: colors.text
                }}
              />
            </div>

            {/* Item List */}
            <div style={{
              maxHeight: '200px',
              overflow: 'auto',
              border: `1px solid ${colors.border}`,
              borderRadius: '8px'
            }}>
              {filteredItems.length === 0 ? (
                <div style={{
                  padding: '2rem',
                  textAlign: 'center',
                  color: colors.textMuted
                }}>
                  {Object.keys(stock).length === 0
                    ? 'No items with stock at this location'
                    : 'No items match your search'
                  }
                </div>
              ) : (
                filteredItems.map(item => {
                  const qty = stock[item.id] || 0;
                  const isSelected = selectedItemId === item.id;
                  return (
                    <button
                      key={item.id}
                      onClick={() => {
                        setSelectedItemId(item.id);
                        setQuantity(1);
                      }}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: isSelected ? `${colors.accent}20` : 'transparent',
                        border: 'none',
                        borderBottom: `1px solid ${colors.border}`,
                        color: colors.text,
                        cursor: 'pointer',
                        textAlign: 'left',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Package size={16} color={isSelected ? colors.accent : colors.textMuted} />
                        <span>{item.name}</span>
                      </div>
                      <span style={{
                        color: colors.textMuted,
                        fontSize: '0.85rem'
                      }}>
                        {qty} available
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* Selected Item & Quantity */}
          {selectedItem && (
            <div style={{
              background: colors.bgInput,
              borderRadius: '12px',
              padding: '1rem',
              marginBottom: '1.5rem'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1rem'
              }}>
                <div>
                  <div style={{ color: colors.text, fontWeight: 'bold' }}>
                    {selectedItem.name}
                  </div>
                  <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                    {availableQty} available at source
                  </div>
                </div>
              </div>

              {/* Quantity */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: colors.textMuted,
                  fontSize: '0.85rem'
                }}>
                  Quantity to Transfer
                </label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input
                    type="number"
                    min="1"
                    max={availableQty}
                    value={quantity}
                    onChange={(e) => setQuantity(Math.min(parseInt(e.target.value) || 1, availableQty))}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      background: colors.bgCard,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      textAlign: 'center',
                      fontSize: '1.1rem',
                      fontWeight: 'bold'
                    }}
                  />
                  <button
                    onClick={() => setQuantity(availableQty)}
                    style={{
                      padding: '0.75rem 1rem',
                      background: colors.bgCard,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.textMuted,
                      cursor: 'pointer',
                      fontSize: '0.85rem'
                    }}
                  >
                    All ({availableQty})
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              color: colors.textMuted,
              fontSize: '0.9rem'
            }}>
              Notes (optional)
            </label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Reason for transfer..."
              style={{
                width: '100%',
                padding: '0.75rem',
                background: colors.bgInput,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                color: colors.text
              }}
            />
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={onClose}
              style={{
                flex: 1,
                padding: '1rem',
                background: colors.bgInput,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                color: colors.text,
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleTransfer}
              disabled={saving || !selectedItemId || quantity <= 0}
              style={{
                flex: 2,
                padding: '1rem',
                background: (saving || !selectedItemId || quantity <= 0) ? colors.bgInput : colors.accent,
                border: 'none',
                borderRadius: '8px',
                color: (saving || !selectedItemId || quantity <= 0) ? colors.textMuted : '#fff',
                fontWeight: 'bold',
                cursor: (saving || !selectedItemId || quantity <= 0) ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              {saving ? (
                <>
                  <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                  Transferring...
                </>
              ) : (
                <>
                  <ArrowRight size={18} />
                  Transfer {quantity > 0 ? quantity : ''}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
